import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import 'leaflet/dist/leaflet.css';
import 'react-leaflet-cluster/dist/assets/MarkerCluster.css';
import 'react-leaflet-cluster/dist/assets/MarkerCluster.Default.css';
import { Loader2, MapPinOff } from 'lucide-react';

const getAddress = (a) => (a.name && a.name !== a.symbol ? a.name : a.propertyAddress) || a.symbol || '';

const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (points.length === 1) map.setView(points[0], 13);
    else if (points.length > 1) map.fitBounds(points, { padding: [40, 40] });
  }, [map, points]);
  return null;
};

export const PropertyMap = ({ assets, API_URL, hideValues, displayCurrency, onOpenDetails }) => {
  const [coords, setCoords] = useState({});
  const [loading, setLoading] = useState(true);
  const addressKey = JSON.stringify([...new Set(assets.map(getAddress).filter(Boolean))]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    fetch(`${API_URL}/api/geocode`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ addresses: JSON.parse(addressKey) }),
      signal: controller.signal
    })
      .then((r) => r.json())
      .then((d) => { setCoords(d.results || {}); setLoading(false); })
      .catch((e) => { if (e.name !== 'AbortError') setLoading(false); });
    return () => controller.abort();
  }, [API_URL, addressKey]);

  const pinned = assets.map((a) => ({ asset: a, addr: getAddress(a), c: coords[getAddress(a)] })).filter((p) => p.c);
  const missing = assets.filter((a) => !loading && !coords[getAddress(a)]);
  const pointsKey = JSON.stringify(pinned.map((p) => [p.c.lat, p.c.lon]));
  const points = useMemo(() => JSON.parse(pointsKey), [pointsKey]);
  const pinnedKey = JSON.stringify(pinned.map(({ asset, addr, c }) => [asset.id, addr, !!asset.disposed, asset.currentPrice || asset.shares || 0, c.lat, c.lon]));
  const handlersRef = useRef({});
  handlersRef.current = { onOpenDetails, displayCurrency };

  // Memoized so parent re-renders don't reset marker positions (which breaks cluster spiderfy)
  const markers = useMemo(() => JSON.parse(pinnedKey).map(([id, addr, disposed, value, lat, lon]) => (
    <CircleMarker
      key={id}
      center={[lat, lon]}
      radius={9}
      pathOptions={disposed
        ? { color: '#64748b', fillColor: '#94a3b8', fillOpacity: 0.8, weight: 2 }
        : { color: '#1d4ed8', fillColor: '#3b82f6', fillOpacity: 0.85, weight: 2 }}
    >
      <Popup>
        <div data-testid={`realestate-map-popup-${id}`} className="space-y-1 min-w-[180px]">
          <div className={`font-semibold text-slate-800 ${disposed ? 'line-through text-slate-500' : ''}`}>{addr}</div>
          {disposed && <div className="text-[10px] font-bold uppercase text-red-500">Sold</div>}
          <div className="text-sm text-slate-600">
            Value: <span className="font-medium">{hideValues ? '•••••' : `$${handlersRef.current.displayCurrency(value)}`}</span>
          </div>
          <button
            data-testid={`realestate-map-open-${id}`}
            onClick={() => handlersRef.current.onOpenDetails && handlersRef.current.onOpenDetails(id)}
            className="mt-1 w-full text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white rounded px-2 py-1 transition-colors"
          >
            Open details
          </button>
        </div>
      </Popup>
    </CircleMarker>
  )), [pinnedKey, hideValues]);

  return (
    <div data-testid="realestate-map-view" className="relative isolate rounded-xl overflow-hidden border border-blue-200">
      <MapContainer center={[39.5, -98.35]} zoom={4} scrollWheelZoom className="h-[420px] md:h-[520px] w-full z-0">
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds points={points} />
        <MarkerClusterGroup chunkedLoading showCoverageOnHover={false} maxClusterRadius={40}>
          {markers}
        </MarkerClusterGroup>
      </MapContainer>
      {loading && (
        <div data-testid="realestate-map-loading" className="absolute top-3 left-1/2 -translate-x-1/2 z-[400] bg-white/90 shadow rounded-full px-3 py-1 text-xs text-slate-600 flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Locating properties… (first time can take a few seconds)
        </div>
      )}
      {missing.length > 0 && (
        <div data-testid="realestate-map-missing" className="absolute bottom-3 left-3 z-[400] bg-white/95 shadow rounded-lg px-3 py-2 text-xs text-slate-600 max-w-[80%]">
          <div className="flex items-center gap-1 font-medium text-slate-700"><MapPinOff className="w-3.5 h-3.5" /> Couldn't locate {missing.length}:</div>
          <div className="truncate">{missing.map(getAddress).join(' • ')}</div>
        </div>
      )}
    </div>
  );
};
